import { useEffect, useRef, useState } from 'react';

import { getQueryClient } from 'components/ProvidersContainer/getQueryClient';
import { useServiceMethodCall } from 'lib/api/hooks/useServiceMethodCall';
import { DataDog } from 'lib/reporter/utils/DataDog';

import { ActionTypes } from '../useServiceMethodCall/ActionTypes';

export const useAccountCreationHandoff = (
  handoffToken: string | null
): { isAuthenticated: boolean; error: boolean } => {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(false);
  const [refreshState, requestRefresh] = useServiceMethodCall({
    serviceName: 'UserAccountAuthentication',
    serviceVersion: '1',
    serviceMethod: 'refresh_access_token',
    args: {},
  });

  // Track the token we've already kicked off a refresh for. useServiceMethodCall returns a NEW
  // requestRefresh reference on every render, so including it in the effect deps (or re-running
  // on the FETCHING re-render) caused an infinite request loop — "Maximum update depth exceeded".
  // Firing once per unique token avoids that without depending on the function identity.
  const requestedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (handoffToken && requestedTokenRef.current !== handoffToken) {
      requestedTokenRef.current = handoffToken;
      requestRefresh({ refresh_token: handoffToken });
    }
    // requestRefresh is intentionally omitted — see requestedTokenRef note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffToken]);

  useEffect(() => {
    switch (refreshState?.status) {
      case ActionTypes.SUCCESS:
        if (refreshState.response?.result) {
          // Abort and drop any in-flight requests issued by the pre-handoff
          // (unauthenticated) session. Their 401s trigger token refreshes whose
          // async failure callback (onTokenRefreshFail) would otherwise fire a
          // moment later — after we've navigated to home — and tear down the
          // brand-new session (STATE_RESET → LoginSplashScreen). Clearing here
          // stops the drain at the source; home refetches fresh against the new
          // valid session.
          const queryClient = getQueryClient();
          queryClient.cancelQueries();
          queryClient.clear();
          setAuthenticated(true);
        } else {
          DataDog.addRumError('account_creation_handoff_empty_result', '', {
            status_code: refreshState.response.status_code,
          });
          setError(true);
        }
        break;
      case ActionTypes.ERROR: {
        const resp = refreshState.response;
        if (resp instanceof Error) {
          DataDog.addRumError(
            'account_creation_handoff_failed',
            resp.stack ?? ''
          );
        } else {
          DataDog.addRumError('account_creation_handoff_failed', '', {
            status_code: (resp as { status_code?: unknown })?.status_code,
          });
        }
        setError(true);
        break;
      }
    }
  }, [refreshState]);

  return { isAuthenticated, error };
};
